import type { Label, ParticipantTaskTemplate } from '@piccolohealth/pbs-common';
import { ChakraV3, NumberedBadge, Spin, useSet } from '@piccolohealth/ui';
import {
  constructZodLiteralUnionType,
  type DateTime,
  dateTimeSchema,
  P,
  z,
} from '@piccolohealth/util';
import React from 'react';
import {
  type RecurrenceOption,
  recurrenceOptions,
} from '../../components/forms/HookedTaskRecurrenceSelect';
import { LabelsStack } from '../../components/generic/LabelsStack';
import { createModal } from '../../components/generic/Modal';
import { PiccoloError } from '../../components/generic/PiccoloError';
import { useCreateParticipantTasksFromTemplatesMutation } from '../../graphql/hooks/useCreateParticipantTaskFromTemplatesMutation';
import { useParticipantTaskTemplatesQuery } from '../../graphql/hooks/useParticipantTaskTemplatesQuery';
import { useAppContext } from '../../hooks/useAppContext';
import { ParticipantTaskTemplateDate } from '../settings/taskTemplates/ParticipantTaskTemplateDate';

type FormValues = {
  name: string;
  dueDate?: DateTime | null;
  requiresDueDate: boolean;
  recurrence?: RecurrenceOption | null;
  labelIds: string[];
};

const schema: z.ZodSchema<FormValues> = z.object({
  name: z.string().min(1, 'Task name is required'),
  requiresDueDate: z.boolean(),
  dueDate: dateTimeSchema.optional(),
  labelIds: z.array(z.string()),
  recurrence: constructZodLiteralUnionType(recurrenceOptions.map((option) => option.raw)),
});

interface ParticipantNoteCreateModalProps {
  participantId: string;
}

export const ParticipantTasksFromTemplatesCreateModal =
  createModal<ParticipantNoteCreateModalProps>((props) => {
    const { participantId, modal } = props;
    const { hide, remove, visible } = modal;
    const { errorToast, successToast, organization } = useAppContext();
    const [selectedTemplateIds, setActions] = useSet<string>();

    const mutation = useCreateParticipantTasksFromTemplatesMutation();

    const { data, isLoading, error } = useParticipantTaskTemplatesQuery({
      organizationId: organization.id,
    });

    const templatesByLabel: {
      label: Label;
      templates: ParticipantTaskTemplate[];
    }[] = React.useMemo(() => {
      const templates = data?.organization?.participantTaskTemplates ?? [];

      const labels = P.uniqBy(
        templates?.flatMap((template) => template.labels) as Label[],
        (l) => l.id,
      );

      return labels.map((label) => ({
        label,
        templates:
          templates?.filter((template) => template.labels.some((l) => l.id === label.id)) ?? [],
      }));
    }, [data?.organization?.participantTaskTemplates]);

    const defaultValue = React.useMemo(() => {
      return templatesByLabel.map(({ label }) => label.name);
    }, [templatesByLabel]);

    const onDeselectAll = React.useCallback(
      (labelId: string) => {
        const templates =
          templatesByLabel.find(({ label }) => label.id === labelId)?.templates ?? [];

        templates.forEach((template) => {
          setActions.remove(template.id);
        });
      },
      [setActions, templatesByLabel],
    );

    const onSelectAll = React.useCallback(
      (labelId: string) => {
        const templates =
          templatesByLabel.find(({ label }) => label.id === labelId)?.templates ?? [];

        templates.forEach((template) => {
          setActions.add(template.id);
        });
      },
      [templatesByLabel, setActions],
    );

    const onToggleSelectAll = React.useCallback(
      (labelId: string) => {
        const templates =
          templatesByLabel.find(({ label }) => label.id === labelId)?.templates ?? [];

        if (templates.map((t) => t.id).every((id) => setActions.has(id))) {
          onDeselectAll(labelId);
        } else {
          onSelectAll(labelId);
        }
      },
      [templatesByLabel, setActions, onDeselectAll, onSelectAll],
    );

    const onCheck = React.useCallback(
      (templateId: string) => {
        if (setActions.has(templateId)) {
          setActions.remove(templateId);
        } else {
          setActions.add(templateId);
        }
      },
      [setActions],
    );

    const onSubmit = React.useCallback(async () => {
      await mutation
        .mutateAsync({
          organizationId: organization.id,
          participantId,
          request: {
            taskTemplateIds: Array.from(selectedTemplateIds),
          },
        })
        .then(() => {
          successToast('Tasks added successfully');
          hide();
        })
        .catch((error) => {
          errorToast(`Error adding tasks: ${error.message}`);
        });
    }, [
      hide,
      mutation,
      organization.id,
      participantId,
      selectedTemplateIds,
      successToast,
      errorToast,
    ]);

    const content = P.run(() => {
      if (isLoading) {
        return <Spin />;
      }

      if (error) {
        return <PiccoloError error={error} />;
      }

      return (
        <ChakraV3.Accordion.Root multiple defaultValue={defaultValue}>
          {templatesByLabel.map(({ label, templates }) => (
            <ChakraV3.Accordion.Item key={label.id} value={label.name}>
              <ChakraV3.Accordion.ItemTrigger px={3}>
                <ChakraV3.HStack w='full'>
                  <ChakraV3.Text fontSize='md' fontWeight='semibold'>
                    {label.name}
                  </ChakraV3.Text>
                  <NumberedBadge count={templates.length} />
                  <ChakraV3.Spacer />
                  <ChakraV3.Button
                    size='xs'
                    variant='outline'
                    onClick={(e) => {
                      e.stopPropagation();

                      onToggleSelectAll(label.id);
                    }}
                  >
                    {templates.map((t) => t.id).every((id) => selectedTemplateIds.has(id))
                      ? 'Deselect all'
                      : 'Select all'}
                  </ChakraV3.Button>
                  <ChakraV3.Accordion.ItemIndicator />
                </ChakraV3.HStack>
              </ChakraV3.Accordion.ItemTrigger>

              <ChakraV3.Accordion.ItemContent px={4} py={2} as={ChakraV3.Stack} gap={2}>
                {templates.map((template) => (
                  <ChakraV3.HStack w='full' key={template.id} gap={4}>
                    <ChakraV3.Checkbox
                      colorPalette='brand'
                      checked={setActions.has(template.id)}
                      onChange={() => onCheck(template.id)}
                    />
                    <ChakraV3.HStack w='full' gap={4}>
                      <ChakraV3.Stack gap={0} w='full'>
                        <ChakraV3.Text fontSize='sm' fontWeight='semibold'>
                          {template.name}
                        </ChakraV3.Text>
                        <ParticipantTaskTemplateDate taskTemplate={template} />
                      </ChakraV3.Stack>
                      <ChakraV3.Spacer />
                      <ChakraV3.Stack flexShrink={0}>
                        <LabelsStack labels={template.labels} />
                      </ChakraV3.Stack>
                    </ChakraV3.HStack>
                  </ChakraV3.HStack>
                ))}
              </ChakraV3.Accordion.ItemContent>
            </ChakraV3.Accordion.Item>
          ))}
        </ChakraV3.Accordion.Root>
      );
    });

    return (
      <ChakraV3.Dialog.Root open={visible} onOpenChange={hide} onExitComplete={remove} size='lg'>
        <ChakraV3.Dialog.Backdrop />
        <ChakraV3.Dialog.Positioner>
          <ChakraV3.Dialog.Content>
            <ChakraV3.DialogTitleSubtitle
              title='Add tasks from templates'
              subtitle='Select task templates to create multiple tasks at once'
            />
            <ChakraV3.Dialog.Body>
              <ChakraV3.Stack>{content}</ChakraV3.Stack>
            </ChakraV3.Dialog.Body>
            <ChakraV3.Dialog.Footer>
              <ChakraV3.HStack>
                <ChakraV3.Button onClick={hide} size='sm' variant='subtle'>
                  Close
                </ChakraV3.Button>
                <ChakraV3.Button
                  onClick={onSubmit}
                  colorPalette='brand'
                  size='sm'
                  loading={mutation.isLoading}
                  loadingText='Adding tasks...'
                >
                  Add tasks
                </ChakraV3.Button>
              </ChakraV3.HStack>
            </ChakraV3.Dialog.Footer>
          </ChakraV3.Dialog.Content>
        </ChakraV3.Dialog.Positioner>
      </ChakraV3.Dialog.Root>
    );
  });
